/**
 * Error Boundary Component
 * Catches React errors and reports them to Sentry
 */

import { Component } from "react";
import * as Sentry from "@sentry/react";
import "./ErrorBoundary.css";

class ErrorBoundaryFallback extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log to console in development
    if (process.env.NODE_ENV === "development") {
      console.error("Error Boundary Caught:", error, errorInfo);
    }

    // Capture in Sentry with additional context
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    });

    this.setState({
      error,
      errorInfo,
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary-container">
          <div className="error-boundary-content">
            <div className="error-boundary-icon">⚠️</div>
            <h1 className="error-boundary-title">Oops! Something went wrong</h1>
            <p className="error-boundary-message">
              We've been notified about this issue and will fix it as soon as
              possible.
            </p>

            <div className="error-boundary-actions">
              <button
                onClick={this.handleReload}
                className="error-boundary-btn error-boundary-btn-primary"
              >
                Reload Page
              </button>
              <button
                onClick={this.handleGoHome}
                className="error-boundary-btn error-boundary-btn-secondary"
              >
                Go to Home
              </button>
            </div>

            {process.env.NODE_ENV === "development" && this.state.error && (
              <details className="error-boundary-details">
                <summary>Error Details (Development Only)</summary>
                <pre className="error-boundary-stack">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Export Sentry-wrapped version
export default Sentry.withErrorBoundary(ErrorBoundaryFallback, {
  fallback: ({ error, resetError }) => (
    <div className="error-boundary-container">
      <div className="error-boundary-content">
        <h2>An error occurred</h2>
        <button onClick={resetError}>Try again</button>
      </div>
    </div>
  ),
  showDialog: false,
});
